/**
 * Čtečka QR štítků přímo v aplikaci.
 *
 * Na Androidu (Chrome) používá vestavěný BarcodeDetector, jinde (iPhone)
 * dekodér jsQR. Záložní cesta: vyfotit štítek a načíst z fotky.
 */
import { useEffect, useRef, useState } from 'react';
import jsQR from '../../vendor/jsQR.cjs';
import { read } from '../../db/db';
import { go } from '../hooks';
import { Notice, Screen } from '../ui';
import { Icon } from '../icons';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Z textu QR kódu vytáhne ID včelstva (odkaz …#/v/<id> nebo samotné ID). */
export function parseColonyLink(text: string): string | null {
  const m = text.match(new RegExp(`#/(?:v|vcelstvo)/(${UUID})`, 'i')) ?? text.trim().match(new RegExp(`^(${UUID})$`, 'i'));
  return m ? m[1].toLowerCase() : null;
}

interface Detector {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

/**
 * Dekódování přes jsQR (iPhone). `center`: jen střed obrazu v plném rozlišení –
 * štítek z větší vzdálenosti má pak na každý čtvereček QR kódu víc bodů.
 * Jinak celý obraz zmenšený na `max` bodů.
 */
function decodeCanvas(canvas: HTMLCanvasElement, source: CanvasImageSource, w: number, h: number, center = false, max = 960): string | null {
  let sx = 0,
    sy = 0,
    sw = w,
    sh = h;
  if (center) {
    const side = Math.round(Math.min(w, h) * 0.75);
    sx = Math.round((w - side) / 2);
    sy = Math.round((h - side) / 2);
    sw = sh = side;
    max = 720;
  }
  const scale = Math.min(1, max / Math.max(sw, sh));
  canvas.width = Math.round(sw * scale);
  canvas.height = Math.round(sh * scale);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' })?.data ?? null;
}

export function ScannerScreen() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const doneRef = useRef(false);
  const [status, setStatus] = useState<'starting' | 'scanning' | 'nocamera'>('starting');
  const [message, setMessage] = useState<{ tone: 'warn' | 'info'; text: string } | null>(null);
  const [denied, setDenied] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const handle = async (text: string) => {
    if (doneRef.current) return;
    const id = parseColonyLink(text);
    if (!id) {
      setMessage({ tone: 'warn', text: 'Tento QR kód není štítek z aplikace.' });
      return;
    }
    const colony = await read.get('colonies', id);
    if (!colony || colony.deletedAt) {
      setMessage({ tone: 'warn', text: 'Včelstvo z tohoto štítku v aplikaci není (štítek z jiného telefonu nebo smazané včelstvo).' });
      return;
    }
    doneRef.current = true;
    navigator.vibrate?.(60);
    go(`vcelstvo/${id}`, true);
  };

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    const Detector = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector;
    let detector: Detector | null = null;

    setStatus('starting');
    setDenied(false);
    setMessage(null);
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('nocamera');
        return;
      }
      try {
        // vyšší rozlišení – bez něj dává iPhone jen 640×480 a vzdálenější štítek se nepřečte
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch (e) {
        setStatus('nocamera');
        if ((e as DOMException)?.name === 'NotAllowedError') setDenied(true);
        return;
      }
      if (stopped) return stream.getTracks().forEach((t) => t.stop());
      // průběžné ostření, kde to telefon umí (jinak se tiše ignoruje)
      const track = stream.getVideoTracks()[0];
      track?.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] }).catch(() => {});
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play().catch(() => {});
      try {
        if (Detector) detector = new Detector({ formats: ['qr_code'] });
      } catch {
        detector = null;
      }
      setStatus('scanning');
      let frame = 0;
      const tick = async () => {
        if (stopped || doneRef.current) return;
        if (video.readyState >= 2 && video.videoWidth) {
          let text: string | null = null;
          try {
            if (detector) text = (await detector.detect(video))[0]?.rawValue ?? null;
            // střídavě střed v plném rozlišení a celý obraz
            else text = decodeCanvas(canvasRef.current, video, video.videoWidth, video.videoHeight, frame++ % 2 === 0);
          } catch {
            detector = null; // vestavěný detektor selhal → jsQR
          }
          if (text) await handle(text);
        }
        timer = window.setTimeout(tick, 100);
      };
      tick();
    })();

    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const fromPhoto = async (file: File) => {
    setMessage(null);
    try {
      const bmp = await createImageBitmap(file);
      const text =
        decodeCanvas(canvasRef.current, bmp, bmp.width, bmp.height, false, 1600) ?? decodeCanvas(canvasRef.current, bmp, bmp.width, bmp.height, true);
      if (text) await handle(text);
      else setMessage({ tone: 'warn', text: 'Na fotce se QR kód nenašel. Zkuste ho vyfotit zblízka a ostře.' });
    } catch {
      setMessage({ tone: 'warn', text: 'Fotku se nepodařilo načíst.' });
    }
  };

  return (
    <Screen title="Načíst QR štítek" backTo={false}>
      {status !== 'nocamera' && (
        <div className="scanner">
          <video ref={videoRef} playsInline muted aria-label="Náhled fotoaparátu" />
          <div className="scanner-frame" />
          {status === 'starting' && <div className="scanner-status">Spouštím fotoaparát…</div>}
        </div>
      )}
      {status === 'scanning' && (
        <p className="muted center">
          Namiřte fotoaparát na štítek na úlu – karta včelstva se otevře sama. Nejlépe ze vzdálenosti 15–40 cm, štítek uprostřed rámečku.
        </p>
      )}
      {denied && <CameraHelp onRetry={() => setAttempt((a) => a + 1)} />}
      {status === 'nocamera' && !denied && !message && (
        <Notice>Fotoaparát se nepodařilo spustit. Štítek můžete vyfotit a načíst z fotky.</Notice>
      )}
      {message && <Notice tone={message.tone}>{message.text}</Notice>}
      <label className="btn wide">
        <Icon name="camera" size={20} /> Vyfotit štítek
        <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = ''; // ať jde stejnou fotku vybrat znovu
            if (f) fromPhoto(f);
          }} />
      </label>
    </Screen>
  );
}

/** Návod, jak povolit fotoaparát – webová aplikace nastavení telefonu sama otevřít nemůže. */
function CameraHelp({ onRetry }: { onRetry: () => void }) {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const installed =
    window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  return (
    <div className="notice warn camera-help">
      <strong>Aplikace nemá povolený fotoaparát.</strong>
      <p>Telefon nedovolí aplikaci otevřít jeho nastavení sama – povolení zapnete takto:</p>
      {ios ? (
        <ol>
          <li>
            Otevřete <b>Nastavení</b> telefonu → <b>Aplikace</b> → <b>Safari</b> (ve starším iOS rovnou <b>Safari</b>).
          </li>
          <li>
            Sjeďte k části <b>Nastavení pro webové stránky</b> → <b>Fotoaparát</b> a zvolte <b>Povolit</b> (nebo <b>Zeptat se</b>).
          </li>
          <li>Vraťte se sem a ťukněte na <b>Zkusit znovu</b>. {installed && 'Když to nepomůže, aplikaci zavřete (přejetím nahoru) a otevřete znovu.'}</li>
        </ol>
      ) : android && installed ? (
        <ol>
          <li>
            Podržte prst na ikoně aplikace na ploše → <b>Informace o aplikaci</b> (ikona ⓘ).
          </li>
          <li>
            <b>Oprávnění</b> → <b>Fotoaparát</b> → <b>Povolit jen při používání aplikace</b>.
          </li>
          <li>Vraťte se sem a ťukněte na <b>Zkusit znovu</b>.</li>
        </ol>
      ) : android ? (
        <ol>
          <li>
            Ťukněte na ikonu vlevo od adresy stránky (zámek / posuvníky) → <b>Oprávnění</b>.
          </li>
          <li>
            <b>Fotoaparát</b> → <b>Povolit</b>.
          </li>
          <li>Ťukněte na <b>Zkusit znovu</b>.</li>
        </ol>
      ) : (
        <p>Klikněte na ikonu vlevo od adresy stránky, povolte fotoaparát a ťukněte na Zkusit znovu.</p>
      )}
      <button type="button" className="btn primary wide" onClick={onRetry}>
        <Icon name="refresh" size={20} /> Zkusit znovu
      </button>
      <p className="small">Bez fotoaparátu jde štítek i vyfotit tlačítkem níže.</p>
    </div>
  );
}
